import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, fileUrl } from "@/lib/api";
import { ClipboardCheck } from "lucide-react";

const STATUS = {
  pending: "border-amber-400/40 text-amber-300 bg-amber-500/10",
  approved: "border-emerald-400/40 text-emerald-300 bg-emerald-500/10",
  rejected: "border-rose-400/40 text-rose-300 bg-rose-500/10",
};

const Row = ({ kind, id, title, img, status, note, to }) => (
  <div data-testid={`my-sub-${id}`} className="flex items-center gap-3 rounded-xl border border-white/10 bg-[#07080A]/60 p-3">
    <div className="h-12 w-10 rounded-md overflow-hidden bg-white/5 flex-shrink-0">{img && <img src={fileUrl(img)} alt="" className="h-full w-full object-cover" />}</div>
    <div className="min-w-0 flex-1">
      <div className="text-sm text-slate-100 truncate">{to ? <Link to={to} className="hover:text-amber-300">{title}</Link> : title}</div>
      <div className="text-[11px] text-slate-500 font-mono uppercase tracking-widest">{kind}</div>
      {note && <div data-testid={`my-sub-note-${id}`} className="text-[11px] text-slate-400 italic mt-0.5">Editor: {note}</div>}
    </div>
    <span data-testid={`my-sub-status-${id}`} className={`text-[10px] font-mono uppercase tracking-widest px-2 py-1 rounded-full border ${STATUS[status] || STATUS.pending}`}>{status}</span>
  </div>
);

export const MySubmissions = () => {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/me/submissions").then((r) => setData(r.data)).catch(() => {}); }, []);
  if (!data) return null;
  const rows = [
    ...data.characters.map((c) => ({ kind: "Character", id: c.submission_id, title: c.name, img: c.image_url, status: c.status, note: c.admin_note, to: c.companion_id ? `/companions/${c.companion_id}` : null, at: c.created_at })),
    ...data.stories.map((s) => ({ kind: s.is_human_written ? "Human-written story" : "Story", id: s.story_id, title: s.title, img: s.cover_url, status: s.approval_status, note: s.admin_note, to: `/story/${s.story_id}`, at: s.created_at })),
  ].sort((a, b) => (b.at || "").localeCompare(a.at || ""));
  const pending = rows.filter((r) => r.status === "pending").length;
  return (
    <div data-testid="my-submissions" className="rounded-3xl border border-white/10 bg-[#0F1117] p-6 md:p-8 mb-12">
      <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
        <div>
          <div className="font-mono text-[10px] uppercase tracking-[0.24em] text-amber-400 mb-1 flex items-center gap-2"><ClipboardCheck className="h-3 w-3" /> Submissions</div>
          <h2 className="font-serif text-2xl font-semibold">Review status</h2>
        </div>
        <span data-testid="my-sub-pending-count" className="text-xs text-slate-400">{pending} awaiting review</span>
      </div>
      {rows.length === 0 ? (
        <div className="text-sm text-slate-500">Nothing submitted yet. <Link to="/write" className="text-rose-300 underline">Write a story</Link> or <Link to="/submit-companion" className="text-rose-300 underline">create a character</Link>.</div>
      ) : (
        <div className="grid md:grid-cols-2 gap-2 max-h-96 overflow-y-auto pr-1">{rows.map((r) => <Row key={r.id} {...r} />)}</div>
      )}
    </div>
  );
};
